import { useEffect, useState } from 'react';
import { Chip } from '../Chip';
import { Row } from '../Row';
import type {
  ChipGroupProps,
  MultipleChipGroupProps,
  SingleChipGroupProps,
} from './ChipGroup.types';

export const ChipGroup = ({
  all,
  options,
  onChange,
  isRound,
  size = 'S',
  value,
  type = 'multiple',
  variant = 'line',
  disableDeselect,
  ...props
}: ChipGroupProps) => {
  const initialValue = type === 'multiple' ? (value as string[]) || [] : (value as string) || '';
  const [isAll, setIsAll] = useState(false);

  const [selected, setSelected] = useState<typeof initialValue>(initialValue);

  const handleSelectChipSingleMode = (chipValue: string) => () => {
    if (selected === chipValue && !disableDeselect) {
      setSelected('');
      return;
    }

    (onChange as SingleChipGroupProps['onChange'])?.(chipValue);
    setSelected(chipValue);
  };

  const handleSelectChipMultipleMode = (chipValue: string) => () => {
    // 선택한 값이 all 일 떄
    if (chipValue === 'all') {
      if (isAll) {
        setIsAll(false);
        setSelected([]);
        (onChange as MultipleChipGroupProps['onChange'])?.([]);
        return;
      }

      setIsAll(true);
      const allOptions = options.map(({ value }) => value);
      setSelected(allOptions);
      (onChange as MultipleChipGroupProps['onChange'])?.(allOptions);
      return;
    }

    // 선택한 값에 포함되어 있을 경우 해당 값 삭제
    if (selected.includes(chipValue)) {
      const copy = [...selected];
      const index = copy.indexOf(chipValue);
      copy.splice(index, 1);

      setSelected(copy);
      (onChange as MultipleChipGroupProps['onChange'])?.(copy);
      return;
    }

    setSelected((prev) => [...prev, chipValue]);
    (onChange as MultipleChipGroupProps['onChange'])?.([...selected, chipValue]);
  };

  // 선택된 값을 체크해서 전체 선택값을 설정
  useEffect(() => {
    if (type === 'single') return;
    setIsAll(options.length === selected?.length);
  }, [selected]);

  // value값이 변경되었을 경우 selectedList에 반영
  useEffect(() => {
    if (!value) return;
    const init = type === 'multiple' ? (value as string[]) || [] : (value as string) || '';
    setSelected(init);
  }, [value]);

  return (
    <Row {...props} css={{ whiteSpace: 'nowrap' }} gap={props.gap || 8}>
      {type === 'multiple' && all && (
        <Chip
          isRound={isRound}
          key='all'
          onClick={handleSelectChipMultipleMode('all')}
          selected={isAll}
          size={size}
          text={'전체'}
          variant={variant}
        />
      )}

      {options.map((option) => (
        <Chip
          isRound={isRound}
          key={option.value}
          onClick={
            type === 'multiple'
              ? handleSelectChipMultipleMode(option.value)
              : handleSelectChipSingleMode(option.value)
          }
          selected={
            type === 'multiple' ? selected.includes(option.value) : selected === option.value
          }
          size={size}
          text={option.label}
          variant={variant}
        />
      ))}
    </Row>
  );
};
